import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/Broadcast.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function Broadcast({ user }) {
  const [members, setMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [sendAll, setSendAll] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [statusType, setStatusType] = useState('');
  const [sending, setSending] = useState(false);
  const [failedRecipients, setFailedRecipients] = useState([]);
  const [pendingText, setPendingText] = useState('');
  const [retrying, setRetrying] = useState(false);

  const roles = user?.roles || [];
  const canAccess = roles.includes('broadcast_sender') || roles.includes('admin');

  useEffect(() => {
    if (!canAccess) return;
    fetchMembers();
    fetchTemplates();
  }, [canAccess]);

  const fetchMembers = async () => {
    const res = await axios.get(`${API_BASE_URL}/users/`);
    setMembers(res.data.filter((m) => m.phone_number));
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/templates/`);
      setTemplates(res.data);
    } catch {}
  };

  const applyTemplate = (template) => {
    const today = new Date().toLocaleDateString('da-DK');
    // Only resolve {{date}} here — {{name}}/{{firstname}}/{{lastname}} must
    // resolve per-recipient on the server, not to the sending agent's name.
    const body = template.body.replace(/\{\{date\}\}/g, today);
    setMessageText(body);
    setShowTemplates(false);
  };

  const toggleMember = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const applyResult = (data) => {
    const { sent, failed_recipients = [], skipped_recipients = [], error: apiError } = data;
    let type = 'success';
    if (sent === 0 && (failed_recipients.length > 0 || skipped_recipients.length > 0)) type = 'error';
    else if (failed_recipients.length > 0 || skipped_recipients.length > 0) type = 'warning';
    setStatusType(type);
    setStatusMsg(
      `Sent to ${sent} member(s).` +
      (failed_recipients.length ? ` ${failed_recipients.length} failed.` : '') +
      (skipped_recipients.length ? ` ${skipped_recipients.length} skipped (no phone number).` : '') +
      (apiError ? ` (${apiError})` : '')
    );
    setFailedRecipients(failed_recipients);
    if (failed_recipients.length === 0) {
      setMessageText('');
      setSelectedIds([]);
      setSendAll(false);
      setPendingText('');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    setSending(true);
    setStatusMsg('');
    setStatusType('');
    setFailedRecipients([]);
    const text = messageText.trim();
    setPendingText(text);
    try {
      const payload = { text };
      if (!sendAll) payload.user_ids = selectedIds;
      const res = await axios.post(`${API_BASE_URL}/messages/broadcast/`, payload);
      applyResult(res.data);
    } catch (err) {
      setStatusType('error');
      setStatusMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleRetryFailed = async () => {
    if (failedRecipients.length === 0) return;
    setRetrying(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/messages/broadcast/`, {
        text: pendingText,
        user_ids: failedRecipients.map((r) => r.id),
      });
      applyResult(res.data);
    } catch (err) {
      setStatusType('error');
      setStatusMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setRetrying(false);
    }
  };

  const handleGiveUp = async () => {
    try {
      await axios.post(`${API_BASE_URL}/messages/broadcast_cancelled/`, {
        recipient_names: failedRecipients.map((r) => r.name),
      });
    } catch {}
    setFailedRecipients([]);
    setMessageText('');
    setSelectedIds([]);
    setSendAll(false);
    setPendingText('');
  };

  const memberName = (m) =>
    `${m.user.first_name} ${m.user.last_name}`.trim() || m.user.username;

  if (!canAccess) return <div className="restricted">You do not have permission to access this page.</div>;

  return (
    <div className="broadcast-page">
      <h2>Broadcast</h2>

      {statusMsg && <div className={`status-msg ${statusType ? `status-${statusType}` : ''}`}>{statusMsg}</div>}

      {failedRecipients.length > 0 && (
        <div className="retry-panel">
          <p>Failed to send to:</p>
          <ul className="retry-list">
            {failedRecipients.map((r) => (
              <li key={r.id}>{r.name} <span className="phone-small">({r.phone_number})</span></li>
            ))}
          </ul>
          <p>Retry sending to these {failedRecipients.length} member(s)?</p>
          <div className="retry-actions">
            <button type="button" className="btn-broadcast" disabled={retrying} onClick={handleRetryFailed}>
              {retrying ? 'Retrying…' : 'Retry'}
            </button>
            <button type="button" className="btn-cancel" disabled={retrying} onClick={handleGiveUp}>
              Give up
            </button>
          </div>
        </div>
      )}

      <div className="template-bar">
        <button type="button" className="btn-template" onClick={() => setShowTemplates(!showTemplates)}>
          {showTemplates ? 'Hide Templates' : 'Select Template'}
        </button>

        {showTemplates && (
          <div className="template-dialog">
            <button className="template-option none-option" onClick={() => { setMessageText(''); setShowTemplates(false); }}>
              — None —
            </button>
            {templates.length === 0 && <p className="no-templates">No templates available.</p>}
            {templates.map((t) => (
              <button key={t.id} className="template-option" onClick={() => applyTemplate(t)}>
                <strong>{t.name}</strong>
                <span className="template-preview">{t.body.slice(0, 80)}{t.body.length > 80 ? '…' : ''}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={handleSend} className="broadcast-form">
        <div className="form-group">
          <label>Message <span className="char-count">{messageText.length}/160</span></label>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type your broadcast message..."
            maxLength={160}
            rows={4}
            required
          />
          {/[{]{2}(name|firstname|lastname)[}]{2}/.test(messageText) && (
            <p className="template-hint">
              Name placeholders will be personalized to each recipient individually.
            </p>
          )}
        </div>

        <div className="recipients-section">
          <h3>Recipients</h3>
          <label className="send-all-label">
            <input type="checkbox" checked={sendAll} onChange={(e) => { setSendAll(e.target.checked); setSelectedIds([]); }} />
            Send to all members ({members.length})
          </label>

          {!sendAll && (
            <div className="member-checklist">
              {members.map((m) => (
                <label key={m.id} className="member-check">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(m.id)}
                    onChange={() => toggleMember(m.id)}
                  />
                  {memberName(m)} <span className="phone-small">({m.phone_number})</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <button type="submit" className="btn-broadcast" disabled={sending || (!sendAll && selectedIds.length === 0)}>
          {sending ? 'Sending…' : `Send to ${sendAll ? 'all' : selectedIds.length} member(s)`}
        </button>
      </form>
    </div>
  );
}

export default Broadcast;
